/**
 * @file Video Preview
 */

import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import ZingTouch from 'zingtouch';

/**
 * Video Preview component
 */
const vVideoPreview = () => {
	const videoPreview = document.querySelector( '.v-video-preview' );

	if ( ! videoPreview ) {
		return;
	}

	gsap.registerPlugin( ScrollTrigger );

	const body         = document.querySelector( 'body' );
	const container    = videoPreview.querySelector( '.container' );
	const wrap         = videoPreview.querySelector( '.v-video-preview__video-wrap' );
	const instructions = videoPreview.querySelector( '.v-video-preview__instructions' );
	const fg           = videoPreview.querySelector( '.v-video-preview__fg' );
	const video        = videoPreview.querySelector( '.v-video-preview__video' );

	const gsapFadeFg     = { opacity: 0, ease: 'power1.inOut' };
	const dragArea       = new ZingTouch.Region( wrap, false, false );
	const scrubSpeed     = 1 / 110;
	let panSpeed         = 0;
	let seekTimeout,
		customSeek,
		playPromise         = false;
	let isPanning        = false;
	let currentScroll    = window.scrollY;
	let prevScroll       = currentScroll;
	let videoCurrentTime = 0;

	/**
	 * Dumb modulo fix
	 *
	 * @param {number} n number to mod
	 * @return {number} result
	 */
	Number.prototype.mod = function( n ) {
		return ( ( this % n ) + n ) % n;
	};

	/*-------------------------------------*\
		Video Helpers
	\*-------------------------------------*/

	/**
	 * Pauses video to enable scrolling scrub
	 */
	const pauseVideo = () => {
		/* if (
			playPromise ||
			! video.duration ||
			video.getAttribute( 'data-active' ) === 'true'
		) {
			return;
		 }*/
		if ( video.getAttribute( 'data-active' ) === 'false' ) {
			return;
		}

		video.pause();
		video.setAttribute( 'data-active', 'false' );
		video.removeEventListener( 'loadedmetadata', pauseVideo );

		/* playPromise = video.play();

		if ( playPromise ) {
			playPromise.then( () => {
				video.pause();
				video.dataset.active = 'true';
			} )
				.catch( () => {
					showFallback();
				} )
				.finally( () => {
					video.removeEventListener( 'loadedmetadata', activateVideo );
				} );
		} */
	};

	/**
	 * Show video poster if video playback fails.
	 */
	const showFallback = () => {
		const fallback = videoPreview.querySelector( '.v-video-preview__fallback' );

		if (
			! fallback ||
			video.classList.contains( 'v-video-preview__video--hidden' )
		) {
			return;
		}

		video.removeEventListener( 'loadedmetadata', pauseVideo );
		video.classList.add( 'v-video-preview__video--hidden' );
		toggleInstructions( 'hide' );
		fallback.classList.remove( 'v-video-preview__fallback--hidden' );
	};

	/**
	 * iOS Power Saver Mode fix
	 */
	const powerSaverPlay = () => {
		if (
			playPromise ||
			video.getAttribute( 'data-active' ) === 'true'
		) {
			return;
		}

		playPromise = video.play();

		playPromise
			.then( () => {
				video.setAttribute( 'data-active', 'true' );
				pauseVideo();
			} )
			.catch( () => showFallback() )
			.finally( () => {
				body.removeEventListener( 'click', powerSaverPlay );
				body.removeEventListener( 'touchstart', powerSaverPlay );
			} );
	};

	/**
	 * Scrub video based on progress of animation
	 *
	 * @param { number } delta The ScrollTrigger progress
	 */
	const videoScrub = ( delta ) => {
		if (
			video.classList.contains( 'v-video-preview__video--hidden' ) ||
			! video.duration ||
			video.seeking ||
			customSeek ||
			isPanning
		) {
			return;
		}

		customSeek  = true;
		clearTimeout( seekTimeout );
		seekTimeout = setTimeout( () => {
			video.currentTime = ( video.currentTime + ( delta * scrubSpeed ) ).mod( video.duration );
			// video.currentTime = ( video.duration * progress ) % video.duration;
			customSeek        = false;
		}, 15 );
	};

	/**
	 * Handles horizontal pan
	 *
	 * @param { Event } e The ZingTouch Pan event
	 */
	const videoPan = ( e ) => {
		if ( ! e.detail ) {
			return;
		}

		const { distanceFromOrigin, directionFromOrigin } = e.detail.data[ 0 ];
		const direction                                   = ( 90 < directionFromOrigin && directionFromOrigin < 270 ) ? -1 : 1;
		let adjustedAngle                                 = directionFromOrigin % 90;
		if ( ( directionFromOrigin > 90 && directionFromOrigin < 180 ) || ( directionFromOrigin > 270 && directionFromOrigin < 360 ) ) {
			adjustedAngle = 90 - adjustedAngle;
		}

		let xDistanceFromOrigin = 0;
		if ( adjustedAngle !== 0 ) {
			const adjustedRad   = adjustedAngle * Math.PI / 180;
			xDistanceFromOrigin = Math.cos( adjustedRad ) * distanceFromOrigin;
		} else if ( directionFromOrigin !== 90 && directionFromOrigin !== 270 ) {
			xDistanceFromOrigin = distanceFromOrigin;
		}

		video.currentTime = ( videoCurrentTime + ( xDistanceFromOrigin * direction * panSpeed ) ).mod( video.duration );
	};

	/**
	 * Toggles instructions overlay
	 *
	 * @param { string } toggle show / hide instructions
	 */
	const toggleInstructions = ( toggle ) => {
		if ( toggle === 'hide' && ! instructions.classList.contains( 'v-video-preview__instructions--hidden' ) ) {
			instructions.classList.add( 'v-video-preview__instructions--hidden' );
		} else {
			instructions.classList.remove( 'v-video-preview__instructions--hidden' );
		}
	};

	/**
	 * Mousedown/touch start
	 */
	const pointerDown = () => {
		toggleInstructions( 'hide' );
		isPanning        = true;
		videoCurrentTime = video.currentTime;
	};

	/**
	 * Mouseup/touchend
	 */
	const pointerUp = () => {
		isPanning = false;
		toggleInstructions( 'show' );
	};

	/*-------------------------------------*\
		Component Fade Animation
	\*-------------------------------------*/

	const fadeTriggerVars  = {
		start:   'top bottom',
		end:     'center center',
		trigger: container,
		scrub:   .5,
	};
	const scrubTriggerVars = {
		start:    '-50% bottom',
		end:      'bottom+=85% top',
		trigger:  wrap,
		scrub:    true,
		onUpdate: () => {
			currentScroll = window.scrollY;
			const delta   = currentScroll - prevScroll;
			videoScrub( delta );
			prevScroll    = currentScroll;
		},
	};

	ScrollTrigger.matchMedia( {
		'(min-width: 1024px)': () => {
			panSpeed = .004166667;
			gsap.timeline( {
				scrollTrigger: fadeTriggerVars,
			} )
				.to( fg, gsapFadeFg );

			ScrollTrigger.create( scrubTriggerVars );
		},
		'(max-width: 1023px)': () => {
			panSpeed = .016666668;
			gsap.timeline( {
				scrollTrigger: {
					start: 'top bottom',
					end:   'center center',
					...fadeTriggerVars,
				},
			} )
				.to( fg, gsapFadeFg );
			ScrollTrigger.create( {
				...scrubTriggerVars,
				start: 'top+=10% bottom',
			} );
		},
	} );

	/*-------------------------------------*\
		Event binding
	\*-------------------------------------*/
	// video.addEventListener( 'loadedmetadata', pauseVideo );

	// Newsletter form imported scripts executing document.write before
	// this js file. Implemented onloadedmetadata attribute on video element directly.
	video.addEventListener( 'play', () => {
		if ( video.paused && video.getAttribute( 'data-active' ) === 'false' ) {
			return;
		}

		video.setAttribute( 'data-active', 'true' );
		pauseVideo();
	} );
	// video.addEventListener( 'suspend', activateVideo );

	// play video when iOS device in power saver mode
	body.addEventListener( 'click', powerSaverPlay );
	body.addEventListener( 'touchstart', powerSaverPlay );

	wrap.addEventListener( 'mousedown', () => {
		pointerDown();
		wrap.style.cursor = 'ew-resize';
	} );
	wrap.addEventListener( 'touchstart', pointerDown );
	window.addEventListener( 'mouseup', () => {
		pointerUp();
		wrap.removeAttribute( 'style' );
	} );
	window.addEventListener( 'touchend', pointerUp );

	dragArea.bind( wrap, 'pan', videoPan );
};

export default vVideoPreview;
